// Debris (catch) fencing above the barriers: chain-link on posts, alpha-tested
// so it costs little and naturally fades out with distance as the mesh thins.

import { Color3, DynamicTexture, Mesh, Scene, StandardMaterial, VertexData } from './babylon';
import type { Track } from '../track/track';
import { getLayout } from '../track/geometry';

const POST_SPACING = 3; // metres; one texture tile per post bay
const BOTTOM = 1.0; // top of the barrier
const TOP = 4.2;

function fenceTexture(scene: Scene): DynamicTexture {
  const S = 256;
  const tex = new DynamicTexture('tex-fence', { width: S, height: S }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, S, S);
  // Chain-link diamonds.
  ctx.strokeStyle = 'rgba(185,190,195,1)';
  ctx.lineWidth = 1.6;
  const step = 16;
  for (let k = -S; k < S * 2; k += step) {
    ctx.beginPath();
    ctx.moveTo(k, 0);
    ctx.lineTo(k + S, S);
    ctx.moveTo(k + S, 0);
    ctx.lineTo(k, S);
    ctx.stroke();
  }
  // Post at the bay edge and rails top and bottom.
  ctx.fillStyle = 'rgba(120,125,130,1)';
  ctx.fillRect(0, 0, 10, S);
  ctx.fillRect(0, 0, S, 6);
  ctx.fillRect(0, S - 6, S, 6);
  tex.hasAlpha = true;
  tex.wrapU = 1;
  tex.wrapV = 1;
  tex.uScale = 1 / POST_SPACING;
  tex.vScale = 1 / (TOP - BOTTOM);
  tex.anisotropicFilteringLevel = 4;
  tex.update(true);
  return tex;
}

export function buildFences(scene: Scene, track: Track): Mesh {
  const geo = getLayout(track);
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (const poly of [geo.barriers.left, geo.barriers.right]) {
    let along = 0;
    let hint: number | undefined;
    const base = positions.length / 3;
    for (let k = 0; k <= poly.length; k++) {
      const p = poly[k % poly.length]!;
      if (k > 0) {
        const q = poly[k - 1]!;
        along += Math.hypot(p.x - q.x, p.z - q.z);
      }
      const h = track.heightNear(p.x, p.z, hint);
      hint = h.index;
      positions.push(p.x, h.y + BOTTOM, p.z, p.x, h.y + TOP, p.z);
      uvs.push(along, 0, along, TOP - BOTTOM);
      if (k > 0) {
        const a = base + (k - 1) * 2;
        indices.push(a, a + 1, a + 2, a + 2, a + 1, a + 3);
      }
    }
  }
  const vd = new VertexData();
  vd.positions = positions;
  vd.uvs = uvs;
  vd.indices = indices;
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  vd.normals = normals;
  const mesh = new Mesh('fences', scene);
  vd.applyToMesh(mesh);
  const mat = new StandardMaterial('fence-mat', scene);
  mat.diffuseTexture = fenceTexture(scene);
  mat.useAlphaFromDiffuseTexture = true;
  mat.transparencyMode = 1;
  mat.alphaCutOff = 0.35;
  mat.backFaceCulling = false;
  mat.twoSidedLighting = true;
  mat.specularColor = new Color3(0.2, 0.2, 0.2);
  mat.emissiveColor = new Color3(0.15, 0.15, 0.15);
  mesh.material = mat;
  mesh.isPickable = false;
  mesh.freezeWorldMatrix();
  return mesh;
}
